import { useCallback } from 'react';

import { PointCreateModalPages } from 'application/components/modals/PointCreateModal/types';
import { useBillingRepositoryInjector } from 'application/providers/DIContainerProvider';
import { ModalTypes } from 'application/providers/ModalProvider';
import AnalyticsService, {
  EventNameGTM,
  ParamNameAMP,
  ParamNameGTM,
  PointEventNameAMP,
  PointParamNameAMP,
  StreamBindFlowEventNameAMP,
} from 'application/services/AnalyticsService';
import CompanyUtils from 'application/utils/CompanyUtils';
import {
  startAcceptanceAdvancedSigning,
  openSigningPreloadTab,
  selectAcceptanceAdvancedContext,
} from 'application/store';
import { RouterUtils } from 'application/providers/RouterProvider';
import { INTEGRATION_PAGE_PATH } from 'application/consts';
import { useAccountCompanyData, useAccountCompanyId, useAccountCompanyIds } from 'application/hooks';
import { useAppDispatch, useAppSelector } from 'application/providers/StoreProvider';
import { SelectPlayerFlowType } from 'application/components/common';
import { DEFAULT_ACCEPTANCE_ADVANCED_ID } from 'application/store/slices/acceptanceAdvancedSlice/consts';

import { Country, PaymentKind, UserFeatures } from 'domain/api/graphql/generated';

import { UseActionButtonsViewModel, UseActionButtonsViewModelProps } from './types';

const URI_FOR_RETURN = `${window.location.origin}/${RouterUtils.generatePath(INTEGRATION_PAGE_PATH, { type: 'sbbol' })}`;

const useActionButtonsViewModel = ({
  targetStreamId,
  collectionId,
  isTemplateMode,
  selectedPoints,
  currentPoint,
  isFullOnboarding,
  setCurrentPoint,
  setCurrentPointStream,
  setInFlow,
  setFlowType,
  openModal,
  replaceModal,
  redirectToStreamCategoryPage,
  setIsStreamBindButtonLoading,
  showCopyStreamError,
  showCopyStreamWithThematicError,
  showCopyStreamWithThematicSuccess,
  copyTemplateStream,
  getSavedStreams,
  getStream,
  onboardingPassedRequest,
  refetchFeaturesOnboarding,
  readStreamWithThematic,
  handleChangeStream,
}: UseActionButtonsViewModelProps): UseActionButtonsViewModel => {
  const dispatch = useAppDispatch();

  const companyId = useAccountCompanyId();
  const companyIds = useAccountCompanyIds();

  const currentCompany = useAccountCompanyData();
  const acceptanceAdvancedContext = useAppSelector(selectAcceptanceAdvancedContext(DEFAULT_ACCEPTANCE_ADVANCED_ID));

  const { watchBillingInfo } = useBillingRepositoryInjector();

  const { canRealizeBillingOperation, hasInactiveLicenses, selectedPaymentKind } = watchBillingInfo(companyId);

  const isDenyCreatePoint = CompanyUtils.isDenyCreatePoint(canRealizeBillingOperation, currentCompany.companyData);

  const handleAddPointClick = () => {
    AnalyticsService.event(PointEventNameAMP.POINT_CREATE_BUTTON_CLICK, {
      [PointParamNameAMP.FROM_PAGE]: PointCreateModalPages.STREAM,
    });

    setInFlow(!isDenyCreatePoint);

    if (isDenyCreatePoint) {
      replaceModal(ModalTypes.DENY_OPERATION, {});
    } else {
      setFlowType(SelectPlayerFlowType.CREATE_POINT_WHILE_BINDING_STREAM);
      replaceModal(ModalTypes.POINT_CREATE, {});
    }
  };

  const sendEvents = (streamId?: string) => {
    AnalyticsService.eventGTM(EventNameGTM.STREAM_START, {
      [ParamNameGTM.ACCOUNT_CREATED_DATE]: currentCompany.companyData?.created,
    });

    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_BOUND, {
      [ParamNameAMP.STREAM_ID]: streamId,
      [ParamNameAMP.COLLECTION_ID]: collectionId,
    });
  };

  const onBindPointsClick = async () => {
    let streamId = targetStreamId;
    const withThematic = readStreamWithThematic();

    if (isTemplateMode) {
      try {
        streamId = (await copyTemplateStream(streamId!, companyId, withThematic))!;

        if (withThematic) {
          showCopyStreamWithThematicSuccess();
        }
      } catch (error) {
        if (withThematic) {
          showCopyStreamWithThematicError();
          return '';
        }

        showCopyStreamError(currentCompany?.country || Country.Ru);
        return '';
      }
    }

    await handleChangeStream(streamId!);
    await getSavedStreams(companyIds);

    if (isTemplateMode) {
      redirectToStreamCategoryPage(streamId!);
    }

    sendEvents(streamId);

    if (targetStreamId) {
      await getStream(targetStreamId);
    }

    setCurrentPointStream({ id: streamId || '' });

    return streamId;
  };

  const bindStream = async () => {
    setIsStreamBindButtonLoading(true);

    const streamId = await onBindPointsClick();

    const isOk = await onboardingPassedRequest({ feature: UserFeatures.WarningStreamFirstStart });

    if (!isOk) {
      throw new Error();
    }

    await refetchFeaturesOnboarding();

    setIsStreamBindButtonLoading(false);

    return streamId;
  };

  const isShowPaymentModal = (streamId?: string) => {
    const hasDevice = Boolean(currentPoint?.device?.type);
    const hasStream = Boolean(currentPoint?.stream?.id) || Boolean(streamId);

    if (selectedPaymentKind === PaymentKind.Sbbol) {
      return hasInactiveLicenses && hasDevice && hasStream;
    }

    return currentPoint?.isEnabled === false && hasDevice && hasStream;
  };

  const handleBindStreamClick = useCallback(async () => {
    const streamId = await bindStream();

    if (isFullOnboarding) {
      if (isShowPaymentModal(streamId)) {
        replaceModal(ModalTypes.PAYMENT_REQUIREMENT, {});
        return;
      }

      const points = selectedPoints ? [...selectedPoints.values()] : [];
      replaceModal(ModalTypes.STREAM_BIND_SUCCESS, { selectedPoints: points });
      return;
    }

    if (!selectedPoints) {
      replaceModal(ModalTypes.STREAM_BIND_SUCCESS, { selectedPoints: [] });
      return;
    }

    const points = [...selectedPoints.values()];

    const hasSingleNoDevicePoint = points.length === 1 && !points[0].device;

    if (hasSingleNoDevicePoint) {
      const isDeviceRequested = currentCompany?.companyData?.deviceRequests?.some(
        (request) => request.pointId === points[0].id
      );
      setCurrentPoint({ ...points[0], stream: { id: streamId } });
      replaceModal(isDeviceRequested ? ModalTypes.DEVICE_BIND_SUCCESS : ModalTypes.SELECT_PLAYER_MODAL, {});
      return;
    }

    replaceModal(ModalTypes.STREAM_BIND_SUCCESS, { selectedPoints: points });
  }, [bindStream, isShowPaymentModal, replaceModal]);

  const openAcceptanceAdvancedSigning = () => {
    const hasActiveTab = acceptanceAdvancedContext?.hasActiveTab;
    const preOpenedTab = hasActiveTab ? null : openSigningPreloadTab();

    dispatch(
      startAcceptanceAdvancedSigning({
        companyId,
        uriForReturn: URI_FOR_RETURN,
        fallbackFn: () => {
          openModal(ModalTypes.SBERBUSSINESS_OPEN, {});
        },
        preOpenedTab,
      })
    );
  };

  return {
    handleAddPointClick,
    handleBindStreamClick,
    openAcceptanceAdvancedSigning,
  };
};

export default useActionButtonsViewModel;
